import { computed, ref } from 'vue';

const GENERIC_ERROR_MESSAGE = 'An error occurred while making the request.';

interface BackendFieldErrors {
    [key: string]: string[];
}

interface ApiErrorType {
    response?: {
        data?: {
            errors?: BackendFieldErrors;
            message?: string;
        };
    };
}

const camelCase = (str: string) => str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());

const camelCaseErrors = (errors: BackendFieldErrors): BackendFieldErrors => Object.fromEntries(
        Object.entries(errors).map(([key, value]) => [camelCase(key), value]),
    );

export const useFormErrorHandling = (visibleFields?: string[]) => {
    const recaptchaError = ref<string | null>(null);
    const backendError = ref<string | null>(null);
    const backendErrors = ref<BackendFieldErrors>({});
    const hiddenFieldErrors = ref<BackendFieldErrors>({});

    const isErrorsOnlyInHiddenFields = computed(() => {
        if (!visibleFields) {
            return false;
        }

        return Object.keys(backendErrors.value).every((key) => !visibleFields.includes(key));
    });

    const clearErrors = (): void => {
        backendError.value = null;
        backendErrors.value = {};
        hiddenFieldErrors.value = {};
    };

    const setRecaptchaError = (error: string | null) => {
        recaptchaError.value = error;
    };

    const setDefaultError = (error: string) => {
        backendError.value = error;
    };

    const setError = (keyName: string, error: string) => {
        backendErrors.value = { ...backendErrors.value, [camelCase(keyName)]: [error] };
    };

    const filterHiddenFieldErrors = (errors: BackendFieldErrors): BackendFieldErrors => {
        if (!visibleFields) {
            return {};
        }

        return Object.fromEntries(
            Object.entries(errors).filter(([key]) => !visibleFields.includes(key)),
        );
    };

    const setErrors = (error: ApiErrorType) => {
        clearErrors();

        if (error?.response?.data?.errors) {
            backendErrors.value = camelCaseErrors(error.response.data.errors);
            hiddenFieldErrors.value = filterHiddenFieldErrors(backendErrors.value);

            if (isErrorsOnlyInHiddenFields.value) {
                backendError.value = GENERIC_ERROR_MESSAGE;
            }
        } else if (error?.response?.data?.message) {
            backendError.value = error.response.data.message;
        } else {
            backendError.value = GENERIC_ERROR_MESSAGE;
        }
    };

    return {
        recaptchaError,
        backendError,
        backendErrors,
        setErrors,
        clearErrors,
        setError,
        setDefaultError,
        setRecaptchaError,
    };
};
